import CommandHandler from 'diagram-js/lib/command/CommandHandler';
import {select, Store} from "@ngrx/store";
import {State} from "@ispm-client/modules/bpmn-modeler/store/state";
import {Base, Shape} from 'diagram-js/lib/model';
import {selectClipboard} from "@ispm-client/modules/bpmn-modeler/store/selectors/clipboard-data.selector";
import {NodeUtils} from "@ispm-client/modules/bpmn-modeler/bpmn/utils/node-utils";
import {PathwaysModeling} from "@ispm-client/modules/bpmn-modeler/bpmn/commands/pathways-modeling";
import {switchMap, take, takeUntil, finalize, takeWhile, delay} from "rxjs/operators";
import {NodeType} from "@ispm/shared/bpmn-io/node-type";
import {isEmpty, get} from 'lodash';
import {ModelingUtil} from "@ispm/shared/bpmn-io/modeling-util";
import {TreeNavigator} from "@ispm/shared/bpmn-io/services/tree-navigator";
import {CopiedBpmnNodeCreatedResult} from "@ispm-client/modules/bpmn-modeler/models/copied-bpmn-node-created-result";
import {NodeCreationResult} from "@ispm-client/modules/bpmn-modeler/models/node-creation-result";
import {TreatmentPlanSelectorResult} from "@ispm-client/modules/bpmn-modeler/models/treatment-plan-selector-result";
import Modeling from "diagram-js/lib/features/modeling/Modeling";
import Selection from 'diagram-js/lib/features/selection/Selection';
import EventBus from "diagram-js/lib/core/EventBus";
import {EventType} from "@ispm/shared/bpmn-io/bpmn-js";
import {selectSpinnerStatus} from "@ispm-client/modules/bpmn-modeler/store/selectors/spinner-state.selector";

export class PasteNodesHandler implements CommandHandler {

  private nodeUtils: NodeUtils;
  private copiedToCreatedNodesMap: Map<string, string>;
  private newSelectionNodes: Array<string>;

  constructor(private elementRegistry,
              private pathwaysModeling: PathwaysModeling,
              private store$: Store<State>,
              private moddle,
              private treeNavigator: TreeNavigator,
              private modeling: Modeling,
                private selection: Selection,
              private eventBus: EventBus) {
    this.nodeUtils = new NodeUtils(this.moddle);
  }

  postExecute(context): any {
    this.copiedToCreatedNodesMap = new Map<string, string>();
    this.newSelectionNodes = [];
    this.store$.pipe(select(selectClipboard), take(1)).subscribe(clipboard => {
      if (clipboard && clipboard.copiedObject) {
        let copiedObject = clipboard.copiedObject;
        if (isEmpty(copiedObject.childrenNodes)) {
          this.pasteNode(context.element, copiedObject);
          this.selectAllElements();

        } else {
          // paste node hierarchy
          this.eventBus.fire(EventType.TOGGLE_SPINNER, {});
          this.store$.pipe(
            select(selectSpinnerStatus),
            takeWhile((status: boolean) => !status, true),
            delay(100),
            finalize(() => {
              this.performPasteNodeHierarchy(context.element, copiedObject);
              this.selectAllElements();
              this.eventBus.fire(EventType.TOGGLE_SPINNER, {});
            })).subscribe();
        }
      }
    });
  }

  private performPasteNodeHierarchy(element, copiedObject) {
    let neighborhoodHeadNodeIDs = [];
    this.pasteNodeHierarchy(element, copiedObject, neighborhoodHeadNodeIDs);
    if (!isEmpty(neighborhoodHeadNodeIDs)) {
      neighborhoodHeadNodeIDs.forEach((neighborhoodHeadNodeID) => {
        const neighborhoodHeadNode = this.elementRegistry.get(neighborhoodHeadNodeID);
        this.treeNavigator.toggleNextElementsVisibility(neighborhoodHeadNode, true);
      })
    }
  }

  private pasteNode(destination, copiedNode, neighborhoodHeadNode?) {
    const type = copiedNode.type;
    const nodeType = copiedNode.nodeType;
    const newCreatedNode = neighborhoodHeadNode ? this.pathwaysModeling.appendNewNode(destination, nodeType, type,
      null, {
        neighborhoodID: neighborhoodHeadNode.id,
        openPanel: false
      }) : this.pathwaysModeling.appendNewNode(destination, nodeType, type,
      null, {openPanel: false});
    if (newCreatedNode && copiedNode['data']) {
      this.copyNodeData(copiedNode, newCreatedNode);
    }
    this.newSelectionNodes.push(newCreatedNode.id);
    if (ModelingUtil.getNodeType(newCreatedNode) === NodeType.neighborhood_entry) {
      const neighborhoodHeadNode = newCreatedNode.outgoing[0].target;
      this.newSelectionNodes.push(neighborhoodHeadNode.id);
      // in case of neighborhood entry we need to copy the auto created neighborhood head data
      const copiedNeighborhoodHeadNode = get(copiedNode, 'childrenNodes[0]', undefined);
      if (copiedNeighborhoodHeadNode) {
        this.copyNodeData(copiedNeighborhoodHeadNode, neighborhoodHeadNode);
      }
    }

    return newCreatedNode.id;
  }

  private copyNodeData(copiedNode: CopiedBpmnNodeCreatedResult<NodeCreationResult | TreatmentPlanSelectorResult>, newCreatedNode: Shape) {
    const data = this.nodeUtils.createNewObjectWithReplacement(copiedNode.data, copiedNode.id, newCreatedNode.id);
    this.pathwaysModeling.createNodeData(newCreatedNode, data, newCreatedNode.nodeType);
  }

  private pasteNodeHierarchy(destination: Shape, copiedHierarchyNode: CopiedBpmnNodeCreatedResult<NodeCreationResult | TreatmentPlanSelectorResult>, neighborhoodHeadNodeIDs: Array<string>, neighborhoodHeadNode?: Shape) {

    const newCreatedNodeID = this.handleNodeCreation(destination, copiedHierarchyNode, neighborhoodHeadNode);
    if (copiedHierarchyNode.type === NodeType.neighborhood_entry) {
      // skip neighborhood head
      copiedHierarchyNode = copiedHierarchyNode.childrenNodes[0];
      if (isEmpty(copiedHierarchyNode.childrenNodes)) {
        neighborhoodHeadNodeIDs.push(this.elementRegistry.get(newCreatedNodeID).outgoing[0].target.id);
      }
    }
    if (!isEmpty(copiedHierarchyNode.childrenNodes)) {
      copiedHierarchyNode.childrenNodes.forEach((child) => {
        const newCreatedNode = this.elementRegistry.get(newCreatedNodeID);
        const targetIncomingGateway = ModelingUtil.getNodeIncomingGateway(newCreatedNode);
        if (targetIncomingGateway) {
          this.newSelectionNodes.push(targetIncomingGateway.id);
        }
        if (ModelingUtil.isNeighborhoodEntryNode(newCreatedNode)) {
          // skip neighborhood head
          neighborhoodHeadNode = newCreatedNode.outgoing[0].target;
          neighborhoodHeadNodeIDs.push(neighborhoodHeadNode.id);
          this.pasteNodeHierarchy(neighborhoodHeadNode, child, neighborhoodHeadNodeIDs, neighborhoodHeadNode);

        } else {
          this.pasteNodeHierarchy(newCreatedNode, child, neighborhoodHeadNodeIDs, neighborhoodHeadNode);
        }
      });
    }
  }


  private handleNodeCreation(source: Shape, copiedHierarchyNode: CopiedBpmnNodeCreatedResult<NodeCreationResult | TreatmentPlanSelectorResult>, neighborhoodHeadNode: Shape) {
    let newCreatedNodeID;

    if (this.copiedToCreatedNodesMap.has(copiedHierarchyNode.id)) {
      // node already existed, need to connect it to source
      newCreatedNodeID = this.copiedToCreatedNodesMap.get(copiedHierarchyNode.id);
      const createdNode = this.elementRegistry.get(newCreatedNodeID);
      // in case node is treatment - append direct connection else append connection to incoming gateway
      const targetIncomingGateway = ModelingUtil.getNodeIncomingGateway(createdNode);
      if (targetIncomingGateway) {
        if (!ModelingUtil.getConnection(source, targetIncomingGateway)) {
          const targets = ModelingUtil.getDirectTreeChildrenNodes(targetIncomingGateway);
          this.modelingConnect(source, targetIncomingGateway, null, null);
          this.pathwaysModeling.appendNewConnection(source, targets, source, targetIncomingGateway);
        }
      } else {
        if (!ModelingUtil.getConnection(source, createdNode)) {
          this.modelingConnect(source, createdNode, null, null);
          this.pathwaysModeling.appendNewConnection(source, createdNode, source);
        }
      }


    } else {
      newCreatedNodeID = this.pasteNode(source, copiedHierarchyNode, neighborhoodHeadNode);
      this.copiedToCreatedNodesMap.set(copiedHierarchyNode.id, newCreatedNodeID);
    }

    return newCreatedNodeID;
  }

  private modelingConnect(source, target, attrs, hints) {
    const connection = ModelingUtil.getConnectionWithDeletionTrackingChange(source, target);
    if (connection) {
      this.pathwaysModeling.removeElements([connection]);
    }

    this.modeling.connect(source, target, attrs, hints);
  }

  private selectAllElements() {
    const selectedElements = this.newSelectionNodes.map((selectedElementID) => this.elementRegistry.get(selectedElementID));
    this.eventBus.fire(EventType.SELECTION_CHANGED,{ oldSelection: this.selection.get(), newSelection: selectedElements });
    this.selection._selectedElements = selectedElements;

  }

}
